import {
  useEstimatePaymentFee,
  useDepositState,
  useDepositActions,
  useDepositValidation,
  useAlfredSupportedOptions,
  useAlfredQuotePreview,
} from "src/hooks";
import { PaymentType } from "src/api";
import { useUrlParamsContext, useDepositWithdrawContext } from "src/contexts";

export const useDeposit = () => {
  // Get context data
  const { balance } = useDepositWithdrawContext();
  const { paymentRouteId } = useUrlParamsContext();
  const { currency } = balance;

  // State management
  const state = useDepositState();
  const {
    amount,
    amountTouched,
    setAmountTouched,
    initiateDepositLoading,
    initiateDepositError,
    bankId,
    setBankId,
  } = state;

  // Currency picker: list of supported fiats + the user's current pick
  const {
    options,
    selected: selectedOption,
    selectedCurrency,
    setSelectedCurrency,
    loading: optionsLoading,
  } = useAlfredSupportedOptions({ type: "deposit" });

  // Live preview from Alfred — `toAmount` is the stable-equivalent we
  // use for platform-side validation/fee. Stays null until the user
  // types a positive amount and the debounced fetch resolves.
  const { quote, loading: quoteLoading } = useAlfredQuotePreview({
    sourceCurrency: selectedOption?.source_currency ?? null,
    targetCurrency: selectedOption?.target_currency ?? null,
    amount,
  });
  const stableAmount = quote ? Number(quote.toAmount) : 0;

  // Platform-side fee runs against the stable-equivalent (USDC/USDT),
  // not the raw fiat input — fee tiers are configured per route in
  // route.currency_id which is always the stable side.
  const { paymentFee, paymentFeeLoading, paymentFeeError } = useEstimatePaymentFee({
    paymentType: PaymentType.Deposit,
    amount: stableAmount,
  });

  // Validation: Alfred limits run against fiat input (selectedOption);
  // platform limits / fee-after-amount run against stableAmount.
  const { errors } = useDepositValidation({
    amount,
    stableAmount,
    paymentFeeError,
    paymentFee,
    selectedOption,
  });

  // Actions
  const actions = useDepositActions({
    amount,
    setAmount: state.setAmount,
    setIsLoading: state.setInitiateDepositLoading,
    setError: state.setInitiateDepositError,
    setSessionData: state.setSessionData,
    setPaymentInstructions: state.setPaymentInstructions,
    currencyPrecision: currency.precision,
    paymentRouteId,
    sourceCurrencyId: selectedCurrency,
    bankId,
  });

  // Today only Colombia (COP / PSE) needs the user to pick a bank
  // upfront — Alfred 422s onramp with `metadata.bankId required`
  // otherwise. Localising the gate keeps every other rail clean.
  const requiresBankId = (selectedOption?.source_currency ?? "").toUpperCase() === "COP";
  const bankIdValid = !requiresBankId || /^\d{4}$/.test(bankId);

  // Compute submit disabled state. Quote must be loaded (we need a
  // valid stable-amount for platform validation) and amount must
  // satisfy both Alfred and platform limits.
  const submitDisabled =
    paymentFeeLoading ||
    quoteLoading ||
    initiateDepositLoading ||
    !!initiateDepositError ||
    !selectedCurrency ||
    !quote ||
    !bankIdValid ||
    Object.values(errors).some((error) => error.length > 0) ||
    !amount;

  return {
    // Session id (for polling transaction status + dev emit endpoint)
    sessionId: state.sessionData?.session_id ?? null,

    // Reset to put the form back when the user closes the expiry
    // modal or hits "start over" after a failed deposit.
    resetDeposit: () => {
      state.setPaymentInstructions(null);
      state.setSessionData(null as never);
      state.setInitiateDepositError(null);
    },

    // Inline rails — bank instructions to render after Next click
    paymentInstructions: state.paymentInstructions,

    // Method-not-available signal — backend returns empty options when
    // the user's KYC country isn't on Alfred's allowlist.
    notAvailableForCountry: !optionsLoading && options.length === 0,

    // Amount (fiat input)
    amount,
    amountTouched,
    setAmountTouched,

    // Live quote (Alfred)
    quote,
    quoteLoading,
    stableAmount,

    // Platform fee (in stable currency)
    paymentFee,
    paymentFeeLoading,

    // Currency (wallet target — what they'll receive)
    currency,

    // Source-currency picker (what they'll pay)
    options,
    selectedOption,
    selectedCurrency,
    setSelectedCurrency,
    optionsLoading,

    // Validation
    errors,
    submitDisabled,

    // Loading and error
    initiateDepositLoading,
    initiateDepositError,

    // Actions
    handleAmountChange: actions.handleAmountChange,
    handleInitiateDeposit: actions.handleInitiateDeposit,

    // CO-only bank-id input (free text). Form renders only when
    // `requiresBankId` is true; `bankIdValid` mirrors the BE DTO
    // shape (`/^\d{4}$/`) and gates submit.
    bankId,
    setBankId,
    requiresBankId,
    bankIdValid,
  };
};
